import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Package } from 'lucide-react';
import { PurchaseOrder } from '../../types';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { customToast } from '../../utils/customToast';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrency } from '../../hooks/useCurrency';

interface ReceiveItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
  onSuccess: () => void;
}

const ReceiveItemsModal: React.FC<ReceiveItemsModalProps> = ({
  isOpen,
  onClose,
  purchaseOrder,
  onSuccess
}) => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [_quantities, _setQuantities] = useState<Record<number, number>>({});

  // Initialize quantities when modal opens or purchase order changes
  useEffect(() => {
    if (isOpen && purchaseOrder) {
      setIsDataLoading(true);
      const initialQuantities = purchaseOrder.items.reduce((acc: Record<number, number>, item) => {
        const remaining = Math.max(0, item.quantity - item.received_quantity);
        acc[item.id] = remaining;
        return acc;
      }, {} as Record<number, number>);
      _setQuantities(initialQuantities);
      setIsDataLoading(false);
    }
  }, [isOpen, purchaseOrder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) {
      return;
    }
    
    const items = purchaseOrder.items
      .filter(item => item.quantity > item.received_quantity)
      .map(item => ({
        id: item.id,
        received_quantity: item.quantity - item.received_quantity
      }));

    if (items.length === 0) {
      customToast.error('All items have already been received');
      return;
    }

    try {
      setLoading(true);
      await purchaseOrderService.receiveItems(purchaseOrder.id, { items });
      customToast.success('Items received successfully!');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error receiving items:', error);
      customToast.error(error.response?.data?.message || 'Failed to receive items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}
      onClick={handleClose}
    >
      <div 
        className={`w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'} ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                <Package className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                  Receive Items
                </h2>
                <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Mark items as received for PO {purchaseOrder.po_number}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Body */}
        <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}>
          {isDataLoading ? (
            // Loading Skeleton
            <div className="p-4 sm:p-5 md:p-6 space-y-6">
              <div className="space-y-4">
                <div className={`h-4 w-64 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
              </div>
              <div className="overflow-x-auto border rounded-lg">
                <div className={`p-0 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  {/* Table header skeleton */}
                  <div className={`px-3 sm:px-4 py-3 grid grid-cols-4 gap-4 border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                    {[...Array(4)].map((_, idx) => (
                      <div key={idx} className={`h-4 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                    ))}
                  </div>
                  {/* Table rows skeleton */}
                  {[...Array(4)].map((_, idx) => (
                    <div key={idx} className={`px-3 sm:px-4 py-3 grid grid-cols-4 gap-4 border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                      {[...Array(4)].map((_, colIdx) => (
                        <div key={colIdx} className={`h-6 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 sm:p-5 md:p-6">
              <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Click "Receive Items" to mark all remaining items as received and update inventory.
            </p>
            
            <div className="overflow-x-auto border rounded-lg">
              <table className={`w-full text-sm ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
                <thead>
                  <tr className={theme === 'dark' ? 'bg-gray-600' : 'bg-gray-50'}>
                    <th className={`px-3 sm:px-4 py-2.5 text-left font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Product</th>
                    <th className={`px-3 sm:px-4 py-2.5 text-right font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Quantity</th>
                    <th className={`px-3 sm:px-4 py-2.5 text-right font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Price</th>
                    <th className={`px-3 sm:px-4 py-2.5 text-right font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Total</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-600' : 'divide-gray-200'}`}>
                  {purchaseOrder.items && purchaseOrder.items.length > 0 ? (
                    purchaseOrder.items.map((item) => {
                      const remaining = (item.quantity || 0) - (item.received_quantity || 0);
                      if (remaining <= 0) return null;
                      
                      const unitPrice = Number(item.unit_price || 0);
                      const totalPrice = remaining * unitPrice;
                      
                      return (
                        <tr key={item.id} className={theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className={`px-3 sm:px-4 py-2.5 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {item.product?.name || item.custom_product_name || 'Unknown Product'}
                          </td>
                          <td className={`px-3 sm:px-4 py-2.5 text-right ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {remaining}
                          </td>
                          <td className={`px-3 sm:px-4 py-2.5 text-right ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {formatCurrency(unitPrice)}
                          </td>
                          <td className={`px-3 sm:px-4 py-2.5 text-right font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {formatCurrency(totalPrice)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className={`px-3 sm:px-4 py-4 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        No items to receive
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </form>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button type="button" onClick={handleClose} disabled={loading} className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} disabled:opacity-50 disabled:cursor-not-allowed`}>
              Cancel
            </button>
            <button type="submit" disabled={loading} onClick={handleSubmit} className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 touch-manipulation ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'} disabled:opacity-50 disabled:cursor-not-allowed`}>
              {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              <span>{loading ? 'Receiving...' : 'Receive Items'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReceiveItemsModal;
